import { useEffect, useMemo, useState } from "react";
import { fetchTours } from "@/lib/api";
import TourCard from "@/components/TourCard";

const CATEGORIES = ["All", "Wellness", "Adventure", "City", "Nature", "Food & Drink"];
const DURATIONS = [
  { label: "Any", value: null },
  { label: "≤ 2 hrs", value: 2 },
  { label: "≤ 4 hrs (half day)", value: 5 },
  { label: "Full day", value: 24 },
];

export default function Tours() {
  const [tours, setTours] = useState([]);
  const [category, setCategory] = useState("All");
  const [maxDuration, setMaxDuration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchTours()
      .then(setTours)
      .catch(() => setTours([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return tours.filter((t) => {
      if (category !== "All" && t.category !== category) return false;
      if (maxDuration !== null && t.duration_hours > maxDuration) return false;
      return true;
    });
  }, [tours, category, maxDuration]);

  return (
    <div data-testid="page-tours">
      {/* HEAD */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-12 sm:pt-28 sm:pb-16">
        <div className="eyebrow text-[var(--pine)] mb-5">The full list</div>
        <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] max-w-3xl">
          A small list,<br/>
          <span className="text-[var(--clay)]">all vetted by us.</span>
        </h1>
        <p className="text-[var(--ink-soft)] mt-6 max-w-xl leading-relaxed">
          Filter by what you're in the mood for. Inquire about any of them — pricing depends on group size and season, and we'll get back to you with a quote.
        </p>
      </section>

      {/* FILTERS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-10">
        <div className="border-y border-[var(--border)] py-6 flex flex-wrap items-center gap-x-8 gap-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="eyebrow text-[var(--ink-soft)] mr-2">Type</span>
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`text-sm pb-1 border-b transition-colors ${
                  category === c
                    ? "text-[var(--pine)] border-[var(--pine)] font-semibold"
                    : "text-[var(--ink-soft)] border-transparent hover:text-[var(--ink)]"
                }`}
                data-testid={`filter-category-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-wrap sm:ml-auto">
            <span className="eyebrow text-[var(--ink-soft)] mr-2">Length</span>
            {DURATIONS.map((d) => (
              <button
                key={d.label}
                onClick={() => setMaxDuration(d.value)}
                className={`text-sm pb-1 border-b transition-colors ${
                  maxDuration === d.value
                    ? "text-[var(--pine)] border-[var(--pine)] font-semibold"
                    : "text-[var(--ink-soft)] border-transparent hover:text-[var(--ink)]"
                }`}
                data-testid={`filter-duration-${d.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32">
        {loading ? (
          <div className="text-[var(--ink-soft)]" data-testid="tours-loading">Loading tours…</div>
        ) : filtered.length === 0 ? (
          <div className="text-[var(--ink-soft)] py-12 border border-[var(--border)] text-center" data-testid="tours-empty">
            No tours match these filters. Try widening them.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="tours-grid">
            {filtered.map((t, i) => (
              <TourCard key={t.slug} tour={t} index={i} />
            ))}
            <div
              className="flex flex-col items-center justify-center text-center p-10 border border-dashed border-[var(--border)] aspect-[4/3] sm:aspect-auto sm:min-h-[420px]"
              data-testid="tours-coming-soon"
            >
              <div className="eyebrow text-[var(--clay)] mb-4">More coming</div>
              <h3 className="font-display text-3xl leading-tight mb-3">New experiences,<br/>landing soon.</h3>
              <p className="text-[var(--ink-soft)] text-sm leading-relaxed max-w-xs">
                We add tours only after we've done them ourselves and trust the people behind them. More on the way — sign up for an inquiry to hear first.
              </p>
            </div>
          </div>
        )}
        <div className="mt-8 text-sm text-[var(--ink-soft)]">
          Showing {filtered.length} of {tours.length} tours
        </div>
      </section>
    </div>
  );
}
